/*
 * Copyright (c) 2023 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, August 18th 2023, 10:26:51 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { withAccessCheck } from "authorization/hoc";
import { BillingConfiguration, PaymentMethod, PaymentAPICredentials, IntegrationCredentialStore } from "lib/schemas";
import { CredentialStoreForm, ChooseExistingCredentials } from "integrations/components";
import {
  useAPIFetchBillingConfigurationPaymentCredentials,
  useAPIMutateBillingConfigurationPaymentCredentials,
} from "service-builder/services";
import { EmphasisText, OSActionButton, APIItemDisplayWrapper, StyledOSDialog } from "ui-widgets";
import { EmptyPaymentCredentialsCTA } from "./EmptyPaymentCredentialsCTA";
import { PaymentCredentialsCard } from "./PaymentCredentialsCard";

interface PaymentProviderCredentialsProps {
  billingConfigurationId: BillingConfiguration["id"];
  paymentMethod: PaymentMethod;
}

export const PaymentProviderCredentials = withAccessCheck(
  {
    resourceId: "managePaymentAPICredentials",
    invalidTitle: "Upgrade your plan to manage payment credentials",
    invalidMessage:
      "You need to upgrade your plan to use custom payment credentials and collect payments directly to your own account on any of our supported payment providers.",
  },
  ({ billingConfigurationId, paymentMethod }: PaymentProviderCredentialsProps) => {
    const [paymentCredentials, setPaymentCredentials] = React.useState<PaymentAPICredentials>(null);

    const {
      fetchPaymentCredentials,
      isMakingAPIRequest: isFetchingPaymentCredentials,
      httpResponseStatusCode: paymentCredentialsHTTPResponseStatusCode,
      isAPIError: isPaymentCredentialsAPIError,
    } = useAPIFetchBillingConfigurationPaymentCredentials();

    React.useEffect(() => {
      fetchPaymentCredentials(billingConfigurationId, paymentMethod.type, setPaymentCredentials);
    }, [billingConfigurationId, paymentMethod.type, setPaymentCredentials, fetchPaymentCredentials]);

    const { removePaymentCredentials, updatePaymentCredentials } = useAPIMutateBillingConfigurationPaymentCredentials();

    const handleUnassignCredential = () => {
      removePaymentCredentials(billingConfigurationId, paymentMethod.type, setPaymentCredentials);
    };

    const handleAssignCredential = (credentialStore: IntegrationCredentialStore) => {
      updatePaymentCredentials(billingConfigurationId, paymentMethod.type, credentialStore.id, setPaymentCredentials);
    };

    return (
      <APIItemDisplayWrapper
        showLoader={isFetchingPaymentCredentials}
        isAPIError={isPaymentCredentialsAPIError}
        httpResponseStatusCode={paymentCredentialsHTTPResponseStatusCode}
      >
        {paymentCredentials?.id ? (
          <PaymentCredentialsCard
            paymentCredentials={paymentCredentials}
            onUnassignCredential={handleUnassignCredential}
          />
        ) : (
          <EmptyPaymentCredentialsCTA
            noDataMessage={
              <>
                You have not assigned specific credentials for this bill configuration to use for integration to{" "}
                <EmphasisText>{paymentMethod.name}</EmphasisText>. If you do not assign specific credentials, by
                default, any available <EmphasisText>{paymentMethod.name}</EmphasisText> credentials set for your
                organization will be used .
              </>
            }
            paymentProvider={paymentMethod}
            ctaButtons={
              <>
                <StyledOSDialog
                  renderOpenButton={({ handleOpen }) => (
                    <OSActionButton onClick={handleOpen} variant="contained" size="large">
                      Assign Existing Credentials
                    </OSActionButton>
                  )}
                  popUpTitle="Assign Existing Credentials"
                >
                  <ChooseExistingCredentials onSelectCredentialStore={handleAssignCredential} />
                </StyledOSDialog>

                <StyledOSDialog
                  renderOpenButton={({ handleOpen }) => (
                    <OSActionButton variant="contained" size="large" onClick={handleOpen}>
                      Create New Credentials
                    </OSActionButton>
                  )}
                  popUpTitle="Create New Credentials"
                >
                  <CredentialStoreForm
                    newCredentialStoreName={`${paymentMethod?.name} credentials`}
                    onSaveCredentialStore={handleAssignCredential}
                  />
                </StyledOSDialog>
              </>
            }
          />
        )}
      </APIItemDisplayWrapper>
    );
  }
);
